/**
 * Config Edit Modal
 * Dynamic modal for editing configuration settings
 * Supports both string arrays and object arrays
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiPlus, FiTrash2 } from 'react-icons/fi';
import { toast } from 'react-toastify';

const ConfigEditModal = ({ isOpen, onClose, config, onSave, isCreating = false }) => {
  const [formData, setFormData] = useState({
    key: '',
    label: '',
    description: '',
    type: 'string_array',
    values: [''],
    objectValues: [{}],
    objectSchema: {
      fields: [
        { name: 'value', label: 'Value', type: 'string', required: true }
      ]
    }
  });

  useEffect(() => {
    if (config) {
      setFormData({
        key: config.key || '',
        label: config.label || '',
        description: config.description || '',
        type: config.type || 'string_array',
        values: config.values?.length > 0 ? config.values : [''],
        objectValues: config.objectValues?.length > 0 ? config.objectValues : [{}],
        objectSchema: config.objectSchema || {
          fields: [
            { name: 'value', label: 'Value', type: 'string', required: true }
          ]
        }
      });
    }
  }, [config]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.label || !formData.type) {
      toast.error('Label and type are required');
      return;
    }

    if (formData.type === 'string_array') {
      // Remove empty values
      const cleanedValues = formData.values.filter(v => v.trim() !== '');
      if (cleanedValues.length === 0) {
        toast.error('At least one value is required');
        return;
      }
      onSave({ ...formData, values: cleanedValues });
    } else {
      // For object arrays, validate that required fields are filled
      const cleanedObjects = formData.objectValues.filter(obj => {
        return formData.objectSchema.fields.some(field => 
          field.required && obj[field.name] && obj[field.name].trim() !== ''
        );
      });
      
      if (cleanedObjects.length === 0) {
        toast.error('At least one complete object is required');
        return;
      }
      onSave({ ...formData, objectValues: cleanedObjects });
    }
  };

  // String array handlers
  const handleAddStringValue = () => {
    setFormData(prev => ({
      ...prev,
      values: [...prev.values, '']
    }));
  };

  const handleRemoveStringValue = (index) => {
    setFormData(prev => ({
      ...prev,
      values: prev.values.filter((_, i) => i !== index)
    }));
  };

  const handleStringValueChange = (index, value) => {
    setFormData(prev => ({
      ...prev,
      values: prev.values.map((v, i) => i === index ? value : v)
    }));
  };

  // Object array handlers
  const handleAddObjectValue = () => {
    const newObj = {};
    formData.objectSchema.fields.forEach(field => {
      newObj[field.name] = '';
    });
    setFormData(prev => ({
      ...prev,
      objectValues: [...prev.objectValues, newObj]
    }));
  };

  const handleRemoveObjectValue = (index) => {
    setFormData(prev => ({
      ...prev,
      objectValues: prev.objectValues.filter((_, i) => i !== index)
    }));
  };

  const handleObjectValueChange = (index, fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      objectValues: prev.objectValues.map((obj, i) => 
        i === index ? { ...obj, [fieldName]: value } : obj
      )
    }));
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <ModalOverlay
        as={motion.div}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <ModalContent
          as={motion.div}
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          <ModalHeader>
            <ModalTitle>
              {isCreating ? 'Create Configuration' : `Edit ${formData.label}`}
            </ModalTitle>
            <CloseButton onClick={onClose}>
              <FiX />
            </CloseButton>
          </ModalHeader>

          <ModalForm onSubmit={handleSubmit}>
            <FormGroup>
              <Label>Label *</Label>
              <Input
                type="text"
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                placeholder="e.g., Blog Categories"
                required
              />
            </FormGroup>

            {isCreating && (
              <FormGroup>
                <Label>Key *</Label>
                <Input
                  type="text"
                  value={formData.key}
                  onChange={(e) => setFormData({ ...formData, key: e.target.value })}
                  placeholder="e.g., blog_categories"
                  required
                />
                <HelpText>Unique identifier (cannot be changed later)</HelpText>
              </FormGroup>
            )}

            <FormGroup>
              <Label>Description</Label>
              <TextArea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description"
                rows={2}
              />
            </FormGroup>

            {isCreating && (
              <FormGroup>
                <Label>Type *</Label>
                <Select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                >
                  <option value="string_array">Simple List (String Array)</option>
                  <option value="object_array">Complex Objects (Object Array)</option>
                </Select>
              </FormGroup>
            )}

            <FormGroup>
              <Label>
                {formData.type === 'string_array' ? 'Values' : 'Objects'}
                <CountBadge>{formData.type === 'string_array' ? formData.values.length : formData.objectValues.length} items</CountBadge>
              </Label>
              
              {formData.type === 'string_array' ? (
                <ValuesContainer>
                  {formData.values.map((value, index) => (
                    <ValueRow key={index}>
                      <ValueInput
                        type="text"
                        value={value}
                        onChange={(e) => handleStringValueChange(index, e.target.value)}
                        placeholder={`Value ${index + 1}`}
                      />
                      {formData.values.length > 1 && (
                        <RemoveButton
                          type="button"
                          onClick={() => handleRemoveStringValue(index)}
                        >
                          <FiTrash2 />
                        </RemoveButton>
                      )}
                    </ValueRow>
                  ))}
                  <AddButton type="button" onClick={handleAddStringValue}>
                    <FiPlus /> Add Value
                  </AddButton>
                </ValuesContainer>
              ) : (
                <ObjectsContainer>
                  {formData.objectValues.map((obj, index) => (
                    <ObjectCard key={index}>
                      <ObjectHeader>
                        <ObjectTitle>Item {index + 1}</ObjectTitle>
                        {formData.objectValues.length > 1 && (
                          <RemoveButton
                            type="button"
                            onClick={() => handleRemoveObjectValue(index)}
                          >
                            <FiTrash2 />
                          </RemoveButton>
                        )}
                      </ObjectHeader>
                      {formData.objectSchema.fields.map((field) => (
                        <ObjectField key={field.name}>
                          <FieldLabel>
                            {field.label}
                            {field.required && <Required>*</Required>}
                          </FieldLabel>
                          <FieldInput
                            type="text"
                            value={obj[field.name] || ''}
                            onChange={(e) => handleObjectValueChange(index, field.name, e.target.value)}
                            placeholder={field.label}
                            required={field.required}
                          />
                        </ObjectField>
                      ))}
                    </ObjectCard>
                  ))}
                  <AddButton type="button" onClick={handleAddObjectValue}>
                    <FiPlus /> Add Object
                  </AddButton>
                </ObjectsContainer>
              )}
            </FormGroup>

            <ModalActions>
              <CancelButton type="button" onClick={onClose}>
                Cancel
              </CancelButton>
              <SaveButton type="submit">
                {isCreating ? 'Create' : 'Save Changes'}
              </SaveButton>
            </ModalActions>
          </ModalForm>
        </ModalContent>
      </ModalOverlay>
    </AnimatePresence>
  );
};

// Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: var(--bg-secondary);
  border-radius: 1rem;
  max-width: 700px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  border: 1px solid var(--border-color);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid var(--border-color);
  position: sticky;
  top: 0;
  background: var(--bg-secondary);
  z-index: 1;
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  color: var(--text-primary);
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
  transition: all 0.2s;

  &:hover {
    background: var(--bg-tertiary);
    color: var(--text-primary);
  }
`;

const ModalForm = styled.form`
  padding: 1.5rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
`;

const CountBadge = styled.span`
  background: rgba(0, 212, 255, 0.15);
  color: var(--accent);
  padding: 0.125rem 0.5rem;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  border-radius: 0.5rem;
  color: var(--text-primary);
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: var(--accent);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  border-radius: 0.5rem;
  color: var(--text-primary);
  font-size: 1rem;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: var(--accent);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  border-radius: 0.5rem;
  color: var(--text-primary);
  font-size: 1rem;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--accent);
  }
`;

const HelpText = styled.small`
  display: block;
  margin-top: 0.25rem;
  color: var(--text-tertiary);
  font-size: 0.85rem;
`;

const ValuesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ValueRow = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const ValueInput = styled(Input)`
  flex: 1;
  margin-bottom: 0;
`;

const RemoveButton = styled.button`
  background: rgba(255, 59, 48, 0.1);
  border: 1px solid rgba(255, 59, 48, 0.3);
  color: #ff3b30;
  padding: 0.75rem;
  border-radius: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 59, 48, 0.2);
  }
`;

const AddButton = styled.button`
  background: rgba(0, 212, 255, 0.1);
  border: 1px solid rgba(0, 212, 255, 0.3);
  color: var(--accent);
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-weight: 600;
  transition: all 0.2s;

  &:hover {
    background: rgba(0, 212, 255, 0.2);
  }
`;

const ObjectsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ObjectCard = styled.div`
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  border-radius: 0.75rem;
  padding: 1rem;
`;

const ObjectHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const ObjectTitle = styled.div`
  font-weight: 600;
  color: var(--text-primary);
`;

const ObjectField = styled.div`
  margin-bottom: 0.75rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 0.85rem;
  color: var(--text-secondary);
  margin-bottom: 0.25rem;
`;

const Required = styled.span`
  color: #ff3b30;
  margin-left: 0.25rem;
`;

const FieldInput = styled(Input)`
  margin-bottom: 0;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--border-color);
`;

const CancelButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  border-radius: 0.5rem;
  color: var(--text-primary);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: var(--bg-primary);
  }
`;

const SaveButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: var(--accent-gradient);
  border: none;
  border-radius: 0.5rem;
  color: white;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    opacity: 0.9;
    transform: translateY(-2px);
  }
`;

ConfigEditModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  config: PropTypes.shape({
    key: PropTypes.string,
    label: PropTypes.string,
    description: PropTypes.string,
    type: PropTypes.oneOf(['string_array', 'object_array']),
    values: PropTypes.arrayOf(PropTypes.string),
    objectValues: PropTypes.arrayOf(PropTypes.object),
    objectSchema: PropTypes.shape({
      fields: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string,
        label: PropTypes.string,
        type: PropTypes.string,
        required: PropTypes.bool
      }))
    })
  }),
  onSave: PropTypes.func.isRequired,
  isCreating: PropTypes.bool
};

ConfigEditModal.defaultProps = {
  config: null,
  isCreating: false
};

export default ConfigEditModal;
